import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  organizations: { id: string; name: string; subdomain?: string | null }[];
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  orgId: string | null;
  orgName: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isDemo: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (name: string, email: string, password: string) => Promise<void>;
  demoLogin: () => Promise<void>;
  logout: () => void;
  switchOrg: (orgId: string) => void;
  refreshToken: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const API_BASE = '/api';

// Выбранная организация живёт в пределах сессии браузера: внутри вкладки она
// сохраняется (в т.ч. при перезагрузке), а при новом открытии сайта пользователь
// попадает в организацию по умолчанию — первую в списке из /auth/me.
// Флаг вычисляется один раз при загрузке страницы.
const IS_NEW_BROWSER_SESSION = (() => {
  try {
    const started = sessionStorage.getItem('hr_session_active');
    sessionStorage.setItem('hr_session_active', '1');
    if (!started) {
      // Чтобы до ответа /auth/me запросы не ушли со старой организацией
      // (api-клиент читает id прямо из localStorage).
      localStorage.removeItem('hr_org_id');
      localStorage.removeItem('hr_org_name');
      return true;
    }
    return false;
  } catch {
    return false;
  }
})();

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('hr_access_token'));
  const [orgId, setOrgId] = useState<string | null>(() => localStorage.getItem('hr_org_id'));
  const [orgName, setOrgName] = useState<string | null>(() => localStorage.getItem('hr_org_name'));
  const [isLoading, setIsLoading] = useState(true);

  const isDemo = token?.startsWith('demo-') || false;
  const isAuthenticated = (!!token && !!user) || isDemo;

  const refreshToken = useCallback(async (): Promise<boolean> => {
    if (isDemo) return true;
    const refresh = localStorage.getItem('hr_refresh_token');
    if (!refresh) return false;
    try {
      const res = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refresh }),
      });
      if (!res.ok) return false;
      const data = await res.json();
      localStorage.setItem('hr_access_token', data.access_token);
      // Только если сервер вернул новый refresh-токен — иначе затирали его
      // строкой "undefined" и следующий вход выбрасывал «Not authenticated».
      if (data.refresh_token) {
        localStorage.setItem('hr_refresh_token', data.refresh_token);
      }
      setToken(data.access_token);
      return true;
    } catch {
      return false;
    }
  }, [isDemo]);

  // Организация выбирается по списку из /auth/me: сохранённую берём, только если
  // она в списке (иначе можно застрять в удалённой организации), а при новом
  // открытии сайта всегда открываем первую — организацию по умолчанию.
  const applyOrgSelection = useCallback((orgs: { id: string; name: string; subdomain?: string | null }[] | undefined) => {
    if (!orgs || orgs.length === 0) return;
    const stored = localStorage.getItem('hr_org_id');
    const remembered = IS_NEW_BROWSER_SESSION ? undefined : orgs.find((o) => o.id === stored);
    const target = remembered ?? orgs[0];
    localStorage.setItem('hr_org_id', target.id);
    localStorage.setItem('hr_org_name', target.name);
    // Поддомен нужен для ссылки на лендинг вакансии
    localStorage.setItem('hr_org_subdomain', target.subdomain || '');
    setOrgId(target.id);
    setOrgName(target.name);
  }, []);

  const fetchMe = useCallback(async (accessToken: string) => {
    if (accessToken.startsWith('demo-')) {
      const { DEMO_USER } = await import('@/mocks/api');
      setUser(DEMO_USER);
      applyOrgSelection(DEMO_USER.organizations);
      setIsLoading(false);
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/auth/me`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (res.ok) {
        const payload = await res.json();
        const me = payload.user ?? payload;
        setUser(me);
        applyOrgSelection(me.organizations);
      } else if (res.status === 401) {
        const refreshed = await refreshToken();
        if (refreshed) {
          const newToken = localStorage.getItem('hr_access_token');
          if (newToken) {
            const res2 = await fetch(`${API_BASE}/auth/me`, {
              headers: { Authorization: `Bearer ${newToken}` },
            });
            if (res2.ok) {
              const payload2 = await res2.json();
              const me = payload2.user ?? payload2;
              setUser(me);
              applyOrgSelection(me.organizations);
            }
          }
        }
      }
    } catch {
      // silently fail
    } finally {
      setIsLoading(false);
    }
  }, [applyOrgSelection, refreshToken]);

  useEffect(() => {
    if (token) {
      fetchMe(token);
    } else {
      setIsLoading(false);
    }
  }, [token, fetchMe]);

  // api-клиент шлёт это событие, когда обновить токен не удалось. Без обработчика
  // пользователь оставался на странице «залогиненным», а данные не грузились.
  useEffect(() => {
    const onForcedLogout = () => {
      setToken(null);
      setUser(null);
      setOrgId(null);
      setOrgName(null);
      localStorage.removeItem('hr_org_id');
      localStorage.removeItem('hr_org_name');
      setIsLoading(false);
    };
    window.addEventListener('auth:logout', onForcedLogout);
    return () => window.removeEventListener('auth:logout', onForcedLogout);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const res = await fetch(`${API_BASE}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err.detail || 'Ошибка входа');
    }
    const data = await res.json();
    localStorage.setItem('hr_access_token', data.access_token);
    localStorage.setItem('hr_refresh_token', data.refresh_token);
    setToken(data.access_token);
    await fetchMe(data.access_token);
  }, [fetchMe]);

  const register = useCallback(async (name: string, email: string, password: string) => {
    const res = await fetch(`${API_BASE}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, email, password }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || err?.message || 'Ошибка регистрации');
    }
    const data = await res.json();
    // Auto-login after successful registration
    if (data.access_token) {
      localStorage.setItem('hr_access_token', data.access_token);
      localStorage.setItem('hr_refresh_token', data.refresh_token || '');
      setToken(data.access_token);
      await fetchMe(data.access_token);
    }
  }, [fetchMe]);

  const demoLogin = useCallback(async () => {
    const { DEMO_TOKEN, DEMO_REFRESH } = await import('@/mocks/api');
    localStorage.setItem('hr_access_token', DEMO_TOKEN);
    localStorage.setItem('hr_refresh_token', DEMO_REFRESH);
    setToken(DEMO_TOKEN);
    await fetchMe(DEMO_TOKEN);
  }, [fetchMe]);

  const logout = useCallback(() => {
    localStorage.removeItem('hr_access_token');
    localStorage.removeItem('hr_refresh_token');
    localStorage.removeItem('hr_org_id');
    localStorage.removeItem('hr_org_name');
    setToken(null);
    setUser(null);
    setOrgId(null);
    setOrgName(null);
  }, []);

  const switchOrg = useCallback(async (newOrgId: string) => {
    const org = user?.organizations?.find((o) => o.id === newOrgId);
    if (org) {
      localStorage.setItem('hr_org_id', org.id);
      localStorage.setItem('hr_org_name', org.name);
      localStorage.setItem('hr_org_subdomain', org.subdomain || '');
      setOrgId(org.id);
      setOrgName(org.name);
      return;
    }
    // In demo mode, also support switching to demo orgs
    if (isDemo) {
      const { mockOrganizations } = await import('@/mocks/api');
      const demoOrg = mockOrganizations.find((o: { id: string }) => o.id === newOrgId);
      if (demoOrg) {
        localStorage.setItem('hr_org_id', demoOrg.id);
        localStorage.setItem('hr_org_name', demoOrg.name);
        setOrgId(demoOrg.id);
        setOrgName(demoOrg.name);
      }
    }
  }, [user, isDemo]);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        orgId,
        orgName,
        isLoading,
        isAuthenticated,
        isDemo,
        login,
        register,
        demoLogin,
        logout,
        switchOrg,
        refreshToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}